// React and Hooks
import React, { useEffect, useState, useCallback } from 'react'

// Icons
import { IoArrowBackOutline } from 'react-icons/io5'
import { MdOutlineAddCircleOutline } from 'react-icons/md'

// Components
import RolePermissionsTable from '../../components/Role'
import CustomToast from '../../components/Custom/CustomToast'
import LinkButton from '../../components/Custom/Buttons/LinkButton'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

const Role = () => {
  // Hooks
  const GoBack = useGoBack()
  const userPermissions = useUserStore((state) => state.user) || []

  // State
  const [isLoading, setLoading] = useState(true)
  const [roleList, setRoleList] = useState([])

  // Fetch roles data
  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInterceptorInstance.get('/roles')
      if (response.data.roles.length > 0) {
        setRoleList(response.data.roles)
      }
    } catch (err) {
      CustomToast({
        message: 'Failed to fetch roles. Please try again!',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  // Handle role deletion
  const handleDelete = useCallback(
    async (id) => {
      if (!userPermissions.includes('role:delete')) {
        CustomToast({
          message: 'You do not have permission to delete roles.',
          type: 'error',
        })
        return
      }

      try {
        await axiosInterceptorInstance.delete(`/roles/role/${id}`)
        setRoleList((prevRoles) => prevRoles.filter((role) => role._id !== id))

        CustomToast({
          message: 'Role deleted successfully.',
          type: 'success',
        })
      } catch (error) {
        if (error.response?.status === 406) {
          CustomToast({
            message:
              'Role is already assigned to user. Please remove the role from user and try again!',
            type: 'error',
          })
        } else {
          CustomToast({
            message: 'Failed to delete role. Please try again!',
            type: 'error',
          })
        }
      }
    },
    [userPermissions],
  )

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={GoBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Role</h1>
          </div>
          {userPermissions.includes('role:create') && (
            <LinkButton
              icon={<MdOutlineAddCircleOutline className='text-xl' />}
              to='/role/add'
              label='Add Role'
            />
          )}
        </div>
      </PageHeader>

      {isLoading ? (
        <p className='text-center my-20'>Loading...</p>
      ) : (
        <RolePermissionsTable
          data={roleList}
          onDelete={handleDelete}
          userPermissions={userPermissions}
        />
      )}
    </div>
  )
}

export default Role
